import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";

const LoanCalculator: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [interestRate, setInterestRate] = useState(0.75);
  const [loanTerm, setLoanTerm] = useState(62);

  const { totalInterest, totalRepayment } = useMemo(() => {
    const calculatedInterest = Math.round(loanAmount * (interestRate / 100) * loanTerm);
    const calculatedRepayment = loanAmount + calculatedInterest;
    return {
      totalInterest: calculatedInterest,
      totalRepayment: calculatedRepayment
    };
  }, [loanAmount, interestRate, loanTerm]);

  return (
    <section id="loan-calculator"  className="w-full  md:py-16 px-6 lg:px-20 flex justify-center">
      <div className="max-w-[1400px] w-full flex flex-col">
        {/* Header Block */}
        <div className="hidden md:flex items-center gap-3 text-neutral-500 uppercase tracking-[0.15em] text-xs md:text-sm font-bold mb-3">
          <span className="w-7 h-[1.5px] bg-neutral-400" />
          <span>EMI Calculator</span>
        </div>

        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[50px] font-kualine md:font-sans font-semibold md:font-black leading-[1.15] tracking-tight text-neutral-900 mt-2 text-center md:text-left">
          See your EMI
          <span className="block text-[#DE3226] mt-2">before you commit.</span>
        </h2>

        {/* Repayment Period Callout */}
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs sm:text-sm font-semibold text-slate-600 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl w-fit">
          <span className="inline-block w-2 h-2 rounded-full bg-[#DE3226]" />
          <span><strong>Loan Repayment Period:</strong> Minimum 62 Days – Maximum 180 Days</span>
          <span className="text-slate-300 hidden sm:inline">|</span>
          <span className="text-slate-500">Flexible repayment for personal & short-term loans</span>
        </div>

        {/* Calculator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mt-8 items-stretch">
          
          {/* Left Side: Sliders Card */}
          <div className="col-span-1 lg:col-span-7 bg-white border border-[#E2E8F0] rounded-[24px] p-5 md:p-6 flex flex-col gap-6 shadow-sm">
            
            {/* Slider 1: Loan Amount */}
            <CustomSlider
              label="Loan Amount"
              value={loanAmount}
              min={10000}
              max={100000}
              step={1000}
              onChange={setLoanAmount}
              displayValue={`₹${loanAmount.toLocaleString('en-IN')}`}
              minLabel="₹10K"
              maxLabel="₹1L"
            />

            {/* Slider 2: Interest Rate */}
            <CustomSlider
              label="Interest Rate"
              value={interestRate}
              min={0.5}
              max={1.0}
              step={0.05}
              onChange={setInterestRate}
              displayValue={`${interestRate.toFixed(2).replace(/\.00$/, '')}%`}
              minLabel="0.5%"
              maxLabel="1%"
            />

            {/* Slider 3: Tenure (62 - 180 days) */}
            <CustomSlider
              label="Tenure"
              value={loanTerm}
              min={62}
              max={180}
              step={1}
              onChange={setLoanTerm}
              displayValue={`${loanTerm} Days`}
              minLabel="62 Days"
              maxLabel="180 Days"
            />

          </div>

          {/* Right Side: Red Summary Card */}
          <div className="col-span-1 lg:col-span-5 bg-[#DE3226] rounded-[24px] p-6 md:p-8 flex flex-col text-white shadow-lg">
            <h3 className="text-xl font-bold tracking-tight mb-3">Loan Summary</h3>
            <div className="w-full h-[1.5px] bg-white/20 mb-4" />

            <div className="flex flex-col gap-3.5 flex-grow">
              {/* Row 1 */}
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm md:text-[15px] font-semibold">Loan Amount</span>
                <span className="text-white font-extrabold text-base md:text-lg">
                  ₹{loanAmount.toLocaleString('en-IN')}
                </span>
              </div>
              {/* Row 2 */}
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm md:text-[15px] font-semibold">Interest Rate</span>
                <span className="text-white font-extrabold text-base md:text-lg">
                  {interestRate.toFixed(2).replace(/\.00$/, '')}%
                </span>
              </div>
              {/* Row 3 */}
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm md:text-[15px] font-semibold">Tenure</span>
                <span className="text-white font-extrabold text-base md:text-lg">
                  {loanTerm} Days
                </span>
              </div>
              {/* Row 4 */}
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm md:text-[15px] font-semibold">Total Interest</span>
                <span className="text-white font-extrabold text-base md:text-lg">
                  ₹{totalInterest.toLocaleString('en-IN')}
                </span>
              </div>

              <div className="w-full h-[1.5px] bg-white/20 my-1.5" />

              {/* Row 5: Total Repayment */}
              <div className="flex justify-between items-center">
                <span className="text-white text-base font-bold">Total Repayment</span>
                <span className="text-white font-black text-xl md:text-2xl">
                  ₹{totalRepayment.toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {/* CTA Button */}
            <Link 
              to="/apply-now"
              className="w-full mt-5 bg-white hover:bg-neutral-50 text-neutral-900 font-extrabold py-3.5 px-6 rounded-[12px] flex items-center justify-between transition-all active:scale-95 shadow-sm"
            >
              <span className="text-[17px]">Get This Offer</span>
              <div className="w-8 h-8 rounded-full bg-[#11261a] text-white flex items-center justify-center shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </div>
            </Link>

          </div>

        </div>
      </div>
    </section>
  );
};

// Range Slider Helper Component
interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (val: number) => void;
  displayValue: string;
  minLabel: string;
  maxLabel: string;
}

const CustomSlider: React.FC<SliderProps> = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  displayValue,
  minLabel,
  maxLabel
}) => {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="flex flex-col w-full text-left gap-2">
      {/* Labels Header */}
      <div className="flex justify-between items-baseline">
        <span className="text-neutral-500 font-semibold text-sm md:text-base">{label}</span>
        <span className="text-neutral-900 font-black text-xl md:text-2xl">{displayValue}</span>
      </div>

      {/* Input Track */}
      <div className="relative w-full flex items-center h-4">
        <input 
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none bg-[#E2E8F0] accent-[#DE3226] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3.5px] [&::-webkit-slider-thumb]:border-[#DE3226] [&::-webkit-slider-thumb]:shadow-md"
          style={{
            background: `linear-gradient(to right, #111111 ${percentage}%, #E2E8F0 ${percentage}%)`
          }}
        />
      </div>

      {/* Range Min/Max Labels */}
      <div className="flex justify-between text-[11px] md:text-xs font-bold text-neutral-400">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
};

export default LoanCalculator;
